import {
  ImageObject,
  SerializableImageObject,
  SerializableDetectedObject,
  RESULT_STATUS_MAP,
  CLASSIFICATION_RESULT,
} from "types/imageRecognition";
import configuredStore from "store/store";
import { log } from "services/logger";
import {
  setMessage,
  setResult,
  setShouldStopImageProcess,
  getModelMetadata,
  getModelMetaDataForLogs,
  setPendingUpload,
  incrementNotSureCount,
  resetNotSureCount,
  setImages,
  clearResult,
} from "store/slices/imageRecognition";
import { getTestTypeValue, getSessionId } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { uploadTestImages } from "services/imageUpload";
import { detectHmo } from "utils/hmoDetection";
import { HMO } from "constants/hmo";
import { sentryLogMessage, sentryLogException } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { WORKER_CONFIG } from '../constants/worker';
import { isWorkerMethodError, sleep } from '../utils/workerHelpers';
import type { Remote } from 'comlink';
import type { PipelineVersion, ModelMetadata } from 'types/models';

interface FilterResponse {
  [key: string]: boolean;
}

export const IMAGE_MAX_TIME_DIFFERENCE_MS = 5000;
export const IMAGE_STACK_SIZE = 7;
export const NOT_SURE_COUNT = 3;
const NOT_SURE_OVERLAY_DELAY = 1500;

// Confidence-based rejection thresholds
export const CONFIDENCE_LOW_THRESHOLD = 0.4;
export const CONFIDENCE_HIGH_THRESHOLD = 0.6;
export const MIN_VALID_IMAGES = 4;

let imagesStack: SerializableDetectedObject[] = [];
let isProcessingStack = false; // Lock to prevent concurrent stack updates
let imageCaptureTimestamp: number | null = null; // Timestamp when first image was captured
let notSureOverlayTimeout: NodeJS.Timeout | null = null; // Timeout for NOT_SURE overlay delay
let reusableImageCanvas: HTMLCanvasElement | null = null;
let reusableImageCanvasCtx: CanvasRenderingContext2D | null = null;

/**
 * Clear the NOT_SURE overlay timeout if it exists
 */
const clearNotSureOverlayTimeout = (): void => {
  if (notSureOverlayTimeout) {
    clearTimeout(notSureOverlayTimeout);
    notSureOverlayTimeout = null;
  }
};

function convertToSerializableImage(img: ImageObject): SerializableImageObject {
  // Reuse a single canvas/context to reduce per-frame allocations on fast devices (iOS Safari).
  if (!reusableImageCanvas) {
    reusableImageCanvas = document.createElement('canvas');
  }
  if (!reusableImageCanvasCtx) {
    reusableImageCanvasCtx = reusableImageCanvas.getContext('2d');
  }
  if (!reusableImageCanvasCtx) {
    throw new Error('Failed to get canvas context');
  }
  reusableImageCanvas.width = img.data.width;
  reusableImageCanvas.height = img.data.height;

  reusableImageCanvasCtx.drawImage(img.data, 0, 0);
  const imageData = reusableImageCanvasCtx.getImageData(
    0,
    0,
    reusableImageCanvas.width,
    reusableImageCanvas.height
  );

  // Clear pixels; keep canvas/context allocated for reuse.
  reusableImageCanvasCtx.clearRect(0, 0, reusableImageCanvas.width, reusableImageCanvas.height);
  
  return {
    imageData,
    width: img.data.width,
    height: img.data.height,
    index: img.index,
  };
}

/**
 * Converts Float32Array crop data from classification model to base64 image string
 * @param cropData - Float32Array containing image pixel data (RGB 0-255 range)
 * @param shape - Array with [height, width, channels] dimensions
 * @returns Base64 data URL string
 */
function cropToBase64(cropData: Float32Array, shape: number[]): string {
  const [height, width, channels = 3] = shape;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Failed to get canvas context for crop conversion');
  }
  
  // Create ImageData - needs RGBA format (4 channels)
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  
  // cropData is RGB (3 channels), ImageData needs RGBA (4 channels)
  for (let i = 0; i < width * height; i++) {
    const srcIdx = i * channels;      // Source: RGB
    const destIdx = i * 4;            // Dest: RGBA
    
    // Clamp values to 0-255 (cropAndResize can produce values slightly outside range)
    data[destIdx]     = Math.max(0, Math.min(255, Math.round(cropData[srcIdx])));     // R
    data[destIdx + 1] = Math.max(0, Math.min(255, Math.round(cropData[srcIdx + 1]))); // G
    data[destIdx + 2] = Math.max(0, Math.min(255, Math.round(cropData[srcIdx + 2]))); // B
    data[destIdx + 3] = 255;  // Alpha (fully opaque)
  }
  
  ctx.putImageData(imageData, 0, 0);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
  
  // Cleanup: clear canvas and remove references to free memory
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  canvas.width = 0;
  canvas.height = 0;
  
  return dataUrl;
}

let tensorflowWorker: Remote<any> | null = null;
let workerReady = false;

export const setTensorflowWorker = (worker: Remote<any> | null) => {  
  tensorflowWorker = worker;
  workerReady = worker !== null;
};

export const resetImageProcessingSession = (): void => {
  imagesStack = [];
  isProcessingStack = false;
  imageCaptureTimestamp = null;
  clearNotSureOverlayTimeout();
};

/**
 * Get pipeline version from worker
 */
export const getPipelineVersion = async (): Promise<PipelineVersion | undefined> => {
  try {
    if (!tensorflowWorker) {
      console.warn('getPipelineVersion: Worker not available');
      return undefined;
    }
    return await callWorkerMethod<PipelineVersion>('pipeline_version');
  } catch (error) {
    sentryLogException({
      error,
      tags: {
        component: 'models',
        category: 'getPipelineVersion',
        ...getModelMetaDataTags(),
      },
      extra: {
        timestamp: new Date().toISOString(),
      },
    });
    console.error('getPipelineVersion: Failed to fetch pipeline version:', error);
    return undefined;
  }
};

/**
 * Get model metadata from Redux store
 */
export const getModelMetadataFromStore = (): ModelMetadata | null => {
  const state = configuredStore.getState();
  return getModelMetadata(state);
};

/**
 * Get combined model + pipeline metadata ready for inclusion in Sentry log `tags`.
 * Returns a flat object with all string values, filtering out empty/undefined values.
 * Suitable for Sentry tags which require string values only.
 */
export const getModelMetaDataTags = (): Record<string, string> => {
  try {
    const modelMetaData = getModelMetaDataForLogs(configuredStore.getState());
    if (!modelMetaData) {
      return {};
    }

    const tags: Record<string, string> = {};
    
    // Add all string fields, filtering out empty strings
    if (modelMetaData.localization_model_name) {
      tags.localization_model_name = modelMetaData.localization_model_name;
    }
    if (modelMetaData.localization_model_version) {
      tags.localization_model_version = modelMetaData.localization_model_version;
    }
    if (modelMetaData.classification_model_name) {
      tags.classification_model_name = modelMetaData.classification_model_name;
    }
    if (modelMetaData.classification_model_version) {
      tags.classification_model_version = modelMetaData.classification_model_version;
    }
    if (modelMetaData.pipeline_name) {
      tags.pipeline_name = modelMetaData.pipeline_name;
    }
    if (modelMetaData.pipeline_version) {
      tags.pipeline_version = modelMetaData.pipeline_version;
    }
    // Convert number to string for pipeline_stack_size
    if (modelMetaData.pipeline_stack_size !== undefined && modelMetaData.pipeline_stack_size !== null) {
      tags.pipeline_stack_size = String(modelMetaData.pipeline_stack_size);
    }

    return tags;
  } catch {
    return {};
  }
};

/**
 * Get combined model + pipeline metadata ready for inclusion in Sentry log `extra` data.
 * Returns a spread-ready object: { model_metadata: {...} } or {} if not available.
 */
export const getModelMetaDataExtra = (): Record<string, unknown> => {
  try {
    const modelMetaData = getModelMetaDataForLogs(configuredStore.getState());
    return modelMetaData ? (modelMetaData as unknown as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

async function waitForWorkerReady(maxWait = WORKER_CONFIG.READINESS_MAX_WAIT): Promise<boolean> {
  if (workerReady && tensorflowWorker) {
    return true;
  }
  
  const startTime = Date.now();
  while ((Date.now() - startTime) < maxWait) {
    if (tensorflowWorker) {
      workerReady = true;
      return true;
    }
    await sleep(WORKER_CONFIG.READINESS_POLL_INTERVAL);
  }
  
  return false;
}

async function callWorkerMethod<T>(
  methodName: string,
  ...args: unknown[]
): Promise<T> {
  if (!tensorflowWorker) {
    throw new Error('Worker not available');
  }
  
  if (!workerReady) {
    const isReady = await waitForWorkerReady();
    if (!isReady) {
      throw new Error('Worker not ready');
    }
  }
  
  try {
    const result = await (tensorflowWorker as any)[methodName](...args);
    return result;
  } catch (error) {
    if (isWorkerMethodError(error)) {
      throw new Error('Worker method not available');
    }
    throw error;
  }
}

export const processImage = async (
  data: ImageObject
) => {
  const { imageRecognition } = configuredStore.getState();
  if (imageRecognition.shouldStopImageProcess || imageRecognition.result) {
    return;
  }

  if (!tensorflowWorker) {
    const testType = getTestTypeValue(configuredStore.getState());
    const testTypeLocaleKey = getTestTypeLocaleKey(testType);
    configuredStore.dispatch(
      setMessage({ msg: `${testTypeLocaleKey}:step3screen:somethingWrong`, status: "ERROR" })
    );
    console.error("[models.ts] processImage: TensorFlow worker not available");
    return;
  }
  if (imagesStack.length >= IMAGE_STACK_SIZE) {
    return;
  }
  
  // Capture timestamp when first image is processed (for metadata)
  const isNewStack = imageCaptureTimestamp === null;
  if (isNewStack) {
    const UTC_OFFSET_HOURS = parseInt(import.meta.env.VITE_UTC_OFFSET_HOURS || '2', 10);
    const UTC_OFFSET_MS = UTC_OFFSET_HOURS * 60 * 60 * 1000;
    imageCaptureTimestamp = Date.now() + UTC_OFFSET_MS;
  }
  
  const processImageLog = `process_image_${data.index}`;
  log(processImageLog, {
    imageId: data.index,
    description: "captured image",
  });

  const testType = getTestTypeValue(configuredStore.getState());
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const serializableImage = convertToSerializableImage(data);

  let filterResult: FilterResponse;

  try {
    filterResult = await callWorkerMethod<FilterResponse>(
      'filtering',
      serializableImage
    );
    
    const { isLowContrast, isBlurred } = filterResult;
    
    log(processImageLog, {
      imageId: data.index,
      isBlurred,
      isLowContrast,
      description: "filtering result for image",
    });

    if (isLowContrast) {
      configuredStore.dispatch(
        setMessage({ msg: `${testTypeLocaleKey}:step3screen:imageDark`, status: "ERROR" })
      );
      return;
    }

    if (isBlurred) {
      configuredStore.dispatch(
        setMessage({ msg: `${testTypeLocaleKey}:step3screen:imageBadQuality`, status: "ERROR" })
      );
      return;
    }
  } catch (error) {
    sentryLogException({
      error,
      tags: {
        component: 'models',
        category: 'filtering',
        ...getModelMetaDataTags(),
      },
      extra: {
        timestamp: new Date().toISOString(),
      },
    });
    configuredStore.dispatch(
      setMessage({ msg: `${testTypeLocaleKey}:step3screen:somethingWrong`, status: "ERROR" })
    );
    return;
  }

  try {
    const localizationResult = await callWorkerMethod<SerializableDetectedObject[]>(
      'localization',
      serializableImage
    );

    // Get sessionId and testType for Sentry logging
    const state = configuredStore.getState();
    const testType = getTestTypeValue(state);

    // Log localization model response to Sentry
    sentryLogMessage({
      message: 'Localization model response',
      level: 'info',
      tags: {
        component: 'models',
        category: 'localization',
        testType,
        ...getModelMetaDataTags(),
      },
      extra: {
        detectedObjectsCount: localizationResult?.length || 0,
        timestamp: new Date().toISOString(),
        localizationResult: localizationResult?.map((obj, idx) => ({
          index: idx,
          class: obj.class,
          confidence: obj.conf,
          bbox: obj.bbox,
          aspectRatio: obj.aspectRatio,
        })) || [],
      },
    });

    if (localizationResult?.length < 2) {
      configuredStore.dispatch(
        setMessage({ msg: `${testTypeLocaleKey}:step3screen:imageWithoutTest`, status: "ERROR" })
      );

      return;
    }

    log(`process_image_${data.index}`, {
      imageId: data.index,
      localizationResult: {
        bbox:localizationResult[0].bbox,
        class:localizationResult[0].class,
        conf:localizationResult[0].conf,
        aspectRatio:localizationResult[0].aspectRatio
      },
      description: "localization result for image",
    });

    configuredStore.dispatch(setMessage({ msg: "", status: "SUCCESS" }));

    // Attach quality scores from filtering to the detection object
    if (typeof filterResult.blurScore === 'number') {
      localizationResult[1].blurScore = filterResult.blurScore;
    }
    if (typeof filterResult.lightingScore === 'number') {
      localizationResult[1].lightingScore = filterResult.lightingScore;
    }

    clasifyImage(localizationResult[1]);
  } catch (error) {
    console.error("Localization error: ", error);
    sentryLogException({
      error,
      tags: {
        component: 'models',
        category: 'localization',
        ...getModelMetaDataTags(),
      },
      extra: {
        timestamp: new Date().toISOString(),
      },
    });
    configuredStore.dispatch(
      setMessage({ msg: `${testTypeLocaleKey}:step3screen:somethingWrong`, status: "ERROR" })
    );
  }
};




/**
 * Delete models from worker memory
 */
export const _deleteModels = async () => {
  await sleep(WORKER_CONFIG.METHOD_CALL_DELAY);
  
  if (tensorflowWorker) {
    try {
      // deleteModels is now async and clears IndexedDB cache
      await (tensorflowWorker as any).deleteModels();
      console.log('Models deleted from worker memory and IndexedDB cache');
    } catch (error) {
      console.error('Error calling worker.deleteModels:', error);
      sentryLogException({
        error,
        tags: {
          component: 'models',
          category: 'deleteModels',
          ...getModelMetaDataTags(),
        },
        extra: {
          timestamp: new Date().toISOString(),
        },
      });
    }
  }
};
interface ClassificationResult {
  result: boolean | 'invalid' | 'rejected';  // Final aggregated result
  resultList: Array<boolean | 'invalid'>;  // Per-image results
  probList: Array<number>;  // Per-image winning probabilities
  statusList: Array<'accepted' | 'rejected'>;
  blurScores?: Array<number>;
  lightingScores?: Array<number>;
}

interface ImageForUpload {
  index: number;
  base64: string;
}

/**
 * Convert imagesStack to imagesForUpload format
 * Converts cropped images from classification stack to base64 format for upload
 */
const convertImagesStackToUploadFormat = (stack: SerializableDetectedObject[]): ImageForUpload[] => {
  return stack
    .map((detectedObj, idx) => {
      if (!detectedObj.crop || !detectedObj.crop.data || !detectedObj.crop.shape) {
        console.warn("clasifyImage: No crop data found for image index", detectedObj.index, "using index", idx);
        return null;
      }
      
      try {
        const base64 = cropToBase64(detectedObj.crop.data, detectedObj.crop.shape);
        return {
          index: idx,
          base64: base64,
        };
      } catch (error) {
        console.error("clasifyImage: Failed to convert crop to base64:", error);
        return null;
      }
    })
    .filter((img): img is ImageForUpload => img !== null);
};

/**
 * Classify image and process stack when full
 */
const clasifyImage = async (
  data: SerializableDetectedObject
): Promise<void> => {
  // Wait for stack lock
  while (isProcessingStack) {
    await sleep(WORKER_CONFIG.STACK_PROCESSING_LOCK_DELAY);
  }
  
  isProcessingStack = true;
  try {
    const currentStack = [...imagesStack];
    const updatedImageStack = getUpdatedStack(currentStack, data);
    imagesStack = updatedImageStack;

    const processImageLog = `process_image_${data.index}`;
    const imageLog = {
      description: `Inside classification, stack length: ${updatedImageStack.length}`,
    };
    log(processImageLog, imageLog);
    console.log("clasifyImage: Updated stack length:", updatedImageStack.length);

    const { imageRecognition } = configuredStore.getState();
    if (
      imagesStack.length === IMAGE_STACK_SIZE &&
      !imageRecognition.shouldStopImageProcess &&
      !imageRecognition.result
    ) {
      await processClassificationStack(data.index);
    } else {
      console.log("clasifyImage: Stack not full yet (", imagesStack.length, "/", IMAGE_STACK_SIZE, ") or shouldStopProcess is true, waiting for more images");
    }
  } finally {
    isProcessingStack = false;
  }
};

/**
 * Process classification when stack is full
 */
async function processClassificationStack(imageIndex: number): Promise<void> {
  const processImageLog = `process_image_${imageIndex}`;
  const imageLog = {
    description: "image filtering and localization finished successfully (stack is full)",
  };
  log(processImageLog, imageLog);

  const state = configuredStore.getState();
  const testType = getTestTypeValue(state);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  
  sentryLogMessage({
    message: 'Image stack is full',
    level: 'info',
    tags: {
      component: 'models',
      category: 'stack_full',
      event_type: SENTRY_EVENT_TYPES.STACK_IS_FULL,
      ...getModelMetaDataTags(),
    },
    extra: {
      imageStackSize: imagesStack.length,
      timestamp: new Date().toISOString(),
    },
  });

  configuredStore.dispatch(setShouldStopImageProcess(true));
  console.log("clasifyImage: Calling classification with", imagesStack.length, "images");

  try {
    const classificationResult = await callWorkerMethod<ClassificationResult>(
      'classification',
      imagesStack
    );

    let {
      result,
      resultList,
      probList,
      statusList,
      blurScores = [],
      lightingScores = [],
    } = classificationResult;

    const testModeRejected = import.meta?.env?.VITE_TEST_MODE_NOT_SURE === 'true';
    if (testModeRejected && result === true) {
      console.log("clasifyImage: TEST MODE - Overriding POSITIVE result to REJECTED for testing");
      result = 'rejected';
    }

    const combinedQualityScores = imagesStack.map((_, idx) => {
      const blurScore = blurScores[idx] ?? 0;
      const lightingScore = lightingScores[idx] ?? 0;
      const normalizedBlur = Math.min(Math.max(blurScore, 0) / 10, 1) * 100;
      const normalizedLighting = Math.min(Math.max(lightingScore, 0), 100);
      return (normalizedBlur * 0.3) + (normalizedLighting * 0.7);
    });

    const maxQualityImageIndex = combinedQualityScores.length > 0
      ? combinedQualityScores.reduce(
          (bestIdx, score, idx, arr) => (score > arr[bestIdx] ? idx : bestIdx),
          0
        )
      : undefined;

    const numericResult =
      result === true ? CLASSIFICATION_RESULT.POSITIVE
      : result === false ? CLASSIFICATION_RESULT.NEGATIVE
      : result === 'rejected' ? CLASSIFICATION_RESULT.REJECTED
      : CLASSIFICATION_RESULT.INVALID;
    
    sentryLogMessage({
      message: 'Classification model response',
      level: 'info',
      tags: {
        component: 'models',
        category: 'classification',
        result: RESULT_STATUS_MAP[numericResult],
        event_type: SENTRY_EVENT_TYPES.MODEL_RESULT,
        ...getModelMetaDataTags(),
      },
      extra: {
        result: RESULT_STATUS_MAP[numericResult],
        resultList,
        probList,
        statusList,
        imagesStackSize: imagesStack.length,
        timestamp: new Date().toISOString(),
      },
    });

    console.log("clasifyImage: Classification result - result:", result);
    console.log("clasifyImage: Individual results:", resultList);
    console.log("clasifyImage: Individual probabilities:", probList);
    console.log("clasifyImage: Status list:", statusList);
 
    log(processImageLog, {
      description: "result of classification images in stack",
      classificationResult: numericResult,
      statusList,
    });

    const shouldShowRejectedRetry =
      result === 'rejected' || statusList.every((status) => status === 'rejected');

    if (shouldShowRejectedRetry) {
      const currentState = configuredStore.getState();
      const currentNotSureCount = currentState.imageRecognition.notSureCount;
      const accumulatedRejectedCount = currentNotSureCount + 1;
      
      console.log("clasifyImage: REJECTED result detected. Count:", accumulatedRejectedCount);
      configuredStore.dispatch(incrementNotSureCount());
      
      const imagesForUpload = convertImagesStackToUploadFormat(imagesStack);

      if (imagesForUpload.length === IMAGE_STACK_SIZE) {
        const rejectedState = configuredStore.getState();
        const uploadTestType = getTestTypeValue(rejectedState);
        const sessionId = getSessionId(rejectedState);
        
        const uploadMetadata = imageCaptureTimestamp ? {
          imageCaptureTimestamp,
        } : undefined;
        
        try {
          await uploadTestImages(imagesForUpload, "REJECTED", uploadMetadata, uploadTestType, sessionId);
          console.log("clasifyImage: REJECTED upload completed successfully");
        } catch (error: any) {
          console.error("clasifyImage: Image upload failed for REJECTED:", error);
        }
      }
      
      imagesStack = [];
      configuredStore.dispatch(setImages([]));
      imageCaptureTimestamp = null;
      
      clearNotSureOverlayTimeout();
      
      if (accumulatedRejectedCount >= NOT_SURE_COUNT) {
        console.log(`clasifyImage: ${NOT_SURE_COUNT} consecutive REJECTED results detected, setting result to INVALID`);
        configuredStore.dispatch(setResult(CLASSIFICATION_RESULT.INVALID));
        return;
      } else {
        configuredStore.dispatch(setResult(CLASSIFICATION_RESULT.REJECTED));
        configuredStore.dispatch(setShouldStopImageProcess(true));
        
        notSureOverlayTimeout = setTimeout(() => {
          configuredStore.dispatch(clearResult());
          configuredStore.dispatch(setShouldStopImageProcess(false));
          notSureOverlayTimeout = null;
          console.log("clasifyImage: REJECTED overlay cleared after delay, ready for new capture");
        }, NOT_SURE_OVERLAY_DELAY);
        
        return;
      }
    }

    console.log("clasifyImage: Setting result to", RESULT_STATUS_MAP[numericResult]);
    configuredStore.dispatch(setResult(numericResult));
    clearNotSureOverlayTimeout();

    const imagesForUpload = convertImagesStackToUploadFormat(imagesStack);

    if (imagesForUpload.length === IMAGE_STACK_SIZE) {
      const state = configuredStore.getState();
      const uploadTestType = getTestTypeValue(state);
      const sessionId = getSessionId(state);
      
      const isClinicalTrialHmo = detectHmo() === HMO.CLINICAL_TRIAL;
      
      if (isClinicalTrialHmo) {
        const uploadMetadata = imageCaptureTimestamp ? {
          imageCaptureTimestamp,
        } : undefined;
        
        configuredStore.dispatch(setPendingUpload({
          images: imagesForUpload,
          metadata: uploadMetadata,
          modelResult: RESULT_STATUS_MAP[numericResult],
          testType: uploadTestType,
          sessionId: sessionId,
          maxQualityImageIndex,
        }));
      } else {
        console.log("clasifyImage: Starting image upload for test");
        
        const uploadMetadata = imageCaptureTimestamp ? {
          imageCaptureTimestamp,
        } : undefined;
        
        uploadTestImages(imagesForUpload, RESULT_STATUS_MAP[numericResult], uploadMetadata, uploadTestType, sessionId, undefined, undefined).catch((error: any) => {
          console.error("clasifyImage: Image upload failed:", error);
          sentryLogException({
            error,
            tags: {
              component: 'models',
              category: 'imageUpload',
              ...getModelMetaDataTags(),
            },
            extra: {
              result: RESULT_STATUS_MAP[numericResult],
              imageCount: imagesForUpload.length,
              timestamp: new Date().toISOString(),
            },
          });
        });
      }
    } else {
      console.warn("clasifyImage: Not uploading images - expected", IMAGE_STACK_SIZE, "but got", imagesForUpload.length);
    }
    
    imagesStack = [];
    imageCaptureTimestamp = null;
    
    if (result === true || result === false) {
      configuredStore.dispatch(resetNotSureCount());
    }
    
    if (result === true || result === false) {
      console.log("clasifyImage: Classification complete, cleaning up models");
      await _deleteModels();
    } else {
      console.log("clasifyImage: Keeping models available for retry (invalid or rejected result)");
    }
  } catch (error) {
    console.error("clasifyImage: Classification error - ", error);
    sentryLogException({
      error,
      tags: {
        component: 'models',
        category: 'classification',
        ...getModelMetaDataTags(),
      },
      extra: {
        stackSize: imagesStack.length,
        timestamp: new Date().toISOString(),
      },
    });
    configuredStore.dispatch(
      setMessage({ msg: `${testTypeLocaleKey}:step3screen:somethingWrong`, status: "ERROR" })
    );
    clearHalfImageStack();
    configuredStore.dispatch(setShouldStopImageProcess(false));
    await _deleteModels();
  }
}

const getUpdatedStack = (
  imagesStackArr: SerializableDetectedObject[],
  imageData: SerializableDetectedObject
) => {
  
  // Only check for removal if stack is at or above the target size
  // This allows the stack to grow to IMAGE_STACK_SIZE first
  let shouldRemoveFirstItem = false;
  if (imagesStackArr.length >= IMAGE_STACK_SIZE) {
    shouldRemoveFirstItem = checkMinimumDuration(imagesStackArr, imageData);
  }

  let updatedArr: SerializableDetectedObject[] = imagesStackArr;
  if (shouldRemoveFirstItem) {
    // Remove oldest item (no need to dispose - crop is TypedArray, not tensor)
    updatedArr = imagesStackArr.slice(1);
  }
  return [...updatedArr, imageData];
};

const checkMinimumDuration = (
  imagesStackArr: SerializableDetectedObject[],
  value?: SerializableDetectedObject
): boolean => {
  if (imagesStackArr.length === 0) {
    return false;
  }
  const itemToCheck = value || imagesStackArr[imagesStackArr.length - 1];
  const firstItemIndex = imagesStackArr[0]?.index || itemToCheck.index;
  const timeDelta = itemToCheck.index - firstItemIndex;
  const shouldRemove = timeDelta > IMAGE_MAX_TIME_DIFFERENCE_MS;
  return shouldRemove;
};

/**
 * Clear half of the image stack (used on classification error)
 */
const clearHalfImageStack = () => {
  const stackHalf = Math.floor(IMAGE_STACK_SIZE / 2);
  imagesStack = imagesStack.slice(stackHalf + 1);
};
